import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import $ from "jquery"
    import { computed, onMounted, onBeforeUnmount, shallowRef, nextTick, watch } from "vue"
    import { toEntityValuesString } from "@common/Common"
    import type { KendoSpreadsheetColumn, KendoSpreadsheetValueSerie } from "@Kendo/Components/Spreadsheet/types"
    import { KendoSpreadsheetUtils } from "@Kendo/Components/Spreadsheet/utils"

    // ======================
    // Props / Emits
    // ======================

    
export default /*@__PURE__*/_defineComponent({
  __name: 'EditableValueSeriesSheet',
  props: {
    propTitle: {},
    propSeries: {},
    propColumns: {}
  },
  emits: ["dataChange"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

    const props = __props

    const emit = __emit

    // ======================
    // DOM + Kendo instance (keep raw)
    // ======================

    const spreadsheetHost = shallowRef<HTMLElement | null>(null)
    const spreadsheet = shallowRef<kendo.ui.Spreadsheet | null>(null)

    // gates
    let initialized = false
    let rebuilding = false

    // ======================
    // Derived (stable)
    // ======================

    const totalRows = computed(() => props.propSeries.length + 1)
    const totalCols = computed(() => props.propColumns.length + 1)

    // ======================
    // Lifecycle
    // ======================

    onMounted(async () =>
    {
        await nextTick()

        const host = spreadsheetHost.value
        if (!host) return

        destroySpreadsheet() // idempotent safety

        const kendo$ = ((window as any).kendo?.jQuery ?? $) as JQueryStatic

        const instance = kendo$(host)
            .kendoSpreadsheet({
                sheetsbar: false,
                toolbar: false,
                rows: totalRows.value,
                columns: totalCols.value,
                rowHeight: 25,
                columnWidth: 150,
                change: () =>
                {
                    // 🔐 avoid false "pending" during rebuild
                    if (!initialized || rebuilding) return
                    emit("dataChange")
                }
            })
            .data("kendoSpreadsheet")

        if (!instance) return

        spreadsheet.value = instance

        rebuildSheet()
        initialized = true
    })

    onBeforeUnmount(() =>
    {
        destroySpreadsheet()
    })

    // ======================
    // Rebuild when inputs change
    // ======================

    // If rm refreshes while editing, your computed props can change.
    // Rebuild spreadsheet content safely (but do NOT emit dataChange).
    watch(
        () => [props.propTitle, props.propColumns, props.propSeries] as const,
        () =>
        {
            if (!spreadsheet.value) return
            rebuildSheet()
        },
        { deep: false }
    )

    // ======================
    // Core build logic
    // ======================

    function rebuildSheet()
    {
        const ss = spreadsheet.value
        if (!ss) return

        rebuilding = true
        try
        {
            const rows: any[] = []

            // Header row
            const headerRow: any = { cells: [{ value: props.propTitle, bold: true }] }

            props.propColumns.forEach((col, idx) =>
            {
                headerRow.cells[idx + 1] = { value: col.Label, bold: true }
            })

            rows.push(headerRow)

            // Data rows
            for (let row = 0; row < props.propSeries.length; row++)
            {
                const cells: any[] = [{ value: props.propSeries[row].Name }]
                props.propSeries[row].Values.forEach((val) => cells.push({ value: val }))
                rows.push({ cells })
            }

            ss.fromJSON({
                sheets: [
                    {
                        name: props.propTitle,
                        rows
                    }
                ]
            })

            const sheet = ss.activeSheet()

            // Freeze headers
            sheet.frozenRows(1)
            sheet.frozenColumns(1)

            // Disable header row & name column
            const colLetter = KendoSpreadsheetUtils.columnToLetter(totalCols.value)
            sheet.range(`A1:${colLetter}1`).enable(false)
            sheet.range(`A1:A${totalRows.value}`).enable(false)

            // Editable range validation
            const editableRange = `B2:${colLetter}${totalRows.value}`

            sheet.range(editableRange).validation({
                dataType: "number",
                comparerType: "custom",
                from: "ISNUMBER(INDIRECT(ADDRESS(ROW(),COLUMN())))",
                allowNulls: true,
                type: "reject",
                messageTemplate: "Only numeric values are allowed."
            })
        } finally
        {
            rebuilding = false
        }
    }

    function destroySpreadsheet()
    {
        const ss = spreadsheet.value
        if (!ss) return
        ss.destroy()
        spreadsheet.value = null
        initialized = false
    }

    // ======================
    // Parent API (ref="sheet")
    // ======================

    function getEntriesForSave(): string[]
    {
        const ss = spreadsheet.value
        if (!ss) return []

        const json = ss.toJSON()
        const sheetJson = json?.sheets?.[0]
        if (!sheetJson?.rows) return []

        return sheetJson.rows
            .slice(1) // skip header row
            .map((row: any) =>
            {
                const rowValues = Array(props.propColumns.length).fill("")

                    ; (row.cells || []).forEach((cell: any) =>
                    {
                        rowValues[cell.index] = cell.value ?? ""
                    })

                const seriesItem = props.propSeries.find((s) => s.Name === rowValues[0])

                return {
                    Id: seriesItem ? seriesItem.Id : null,
                    Values: rowValues.slice(1)
                }
            })
            .filter((a: any) => a.Id != null)
            .map((a: any) => toEntityValuesString("", a.Id, a.Values))
    }

    __expose({ getEntriesForSave })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", {
      ref_key: "spreadsheetHost",
      ref: spreadsheetHost,
      class: "spreadsheet-container"
    }, null, 512 /* NEED_PATCH */)
  ]))
}
}

})